import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    TicketIcon,
    EyeIcon,
    EllipsisVerticalIcon,
    ArrowsRightLeftIcon,
} from '@heroicons/react/20/solid'
import { Button } from '../../components/button'
import { Input, InputGroup } from '../../components/input'
import { Select } from '../../components/select'
import { Badge } from '../../components/badge'
import { Divider } from '../../components/divider'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/table'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from '../../components/dropdown'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from '../../components/custom/ResponseDialog'
import {
    Pagination,
    PaginationList,
    PaginationNext,
    PaginationPage,
    PaginationPrevious,
} from '../../components/pagination'
import { getPageRange } from '../../utils/paginationHelper'
import loadingImage from '../../assets/images/boat-loading.gif'
import { formatDateOnly } from 'utils/common'
import { UserIcon } from '@heroicons/react/16/solid'
import { GET_ROUND_ROUTE_PRICE } from 'graphql/routeTrip/queries'
import { GET_LOCATIONS } from 'graphql/stationDistanceConfig/queries'
import { HANDLE_ROUND_ROUTE_PLAN } from 'graphql/routeTrip/mutations'
import { SeatLayoutDropdown } from 'pages/metroRoutes/metroRoutesForm/SeatLayoutDropdown'

export default function RoundTrip() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchString, setSearchString] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [categoryFilter, setCategoryFilter] = useState('')
    const [stationFromFilter, setStationFromFilter] = useState('')
    const [stationToFilter, setStationToFilter] = useState('')
    const [activateDialog, setActivateDialog] = useState({ open: false, id: null, name: '' })
    const [deactivateDialog, setDeactivateDialog] = useState({ open: false, id: null, name: '' })
    const [successDialog, setSuccessDialog] = useState({ open: false, message: '' })
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' })
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [locations, setLocations] = useState([])
    const [statusCodes, setStatusCodes] = useState([])
    const [seatLayoutFilter, setSeatLayoutFilter] = useState('')
    const [seatLayouts, setSeatLayouts] = useState([])

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const { data, loading, error, refetch } = useQuery(GET_ROUND_ROUTE_PRICE, {
        variables: {
            page: {
                offset: page * rowsPerPage,
                limit: rowsPerPage,
            },
        },
        fetchPolicy: 'network-only',
    })

    // Update round route Status
    const [updateRoundRouteStatus, { loading: activateLoading }] = useMutation(HANDLE_ROUND_ROUTE_PLAN, {
        onCompleted: data => {
            if (data?.enableDisbleRoundTripPrice?.success) {
                setSuccessDialog({
                    open: true,
                    message: data.enableDisbleRoundTripPrice.message || 'route routes plan activated successfully',
                })
                setShowSucDialog(true)
                refetch()
            } else {
                setErrorDialog({
                    open: true,
                    message: data?.enableDisbleRoundTripPrice?.message || 'Failed to activate route routes plan',
                })
                setShowErrDialog(true)
            }
            setActivateDialog({ open: false, id: null })
            setDeactivateDialog({ open: false, id: null })
        },
        onError: error => {
            setErrorDialog({
                open: true,
                message: error.message || 'An error occurred while activating route routes plan',
            })
            setShowErrDialog(true)
            setActivateDialog({ open: false, id: null })
            setDeactivateDialog({ open: false, id: null })
        },
    })

    const roundRoutes = data?.getRoundRoutePrice?.round_prices || []
    const totalCount = data?.getRoundRoutePrice?.total_count || 0
    const totalPages = Math.ceil(totalCount / rowsPerPage)

    const { data: allLocations } = useQuery(GET_LOCATIONS, {
        variables: {
            page: { limit: 100, offset: 0 },
        },
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.searchLocation?.locations) {
                setLocations(data.searchLocation.locations)
            }
        },
    })

    const filteredRoundTrips = roundRoutes.filter(rrt => {
        const matchesStatus = !statusFilter || rrt.status_code === statusFilter

        const matchStationFrom = !stationFromFilter || rrt.start_location_id === stationFromFilter

        const matchStationTo = !stationToFilter || rrt.end_location_id === stationToFilter

        const matchSeatLayout = !seatLayoutFilter || rrt.seat_layout_id === seatLayoutFilter

        return matchesStatus && matchStationFrom && matchStationTo && matchSeatLayout
    })

    const onClickSearch = () => {
        setPage(0)
    }

    const onClickClear = () => {
        setSearchString('')
        setStatusFilter('')
        setCategoryFilter('')
        setStationFromFilter('')
        setStationToFilter('')
        setSeatLayoutFilter('')
        setPage(0)
    }

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const getStatusBadge = roundRouteStatus => {
        const statusMap = {
            ACT: { color: 'emerald', label: 'Active' },
            INA: { color: 'rose', label: 'Inactive' },
        }
        const status = statusMap[roundRouteStatus] || { color: 'zinc', label: roundRouteStatus }
        return <Badge color={status.color}>{t(status.label)}</Badge>
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessDialog({ open: false, message: '' })
        setShowErrDialog(false)
        setErrorDialog({ open: false, message: '' })
    }

    const handleActivate = id => {
        setActivateDialog({
            open: true,
            id,
        })
    }

    const handleDeactivate = id => {
        setDeactivateDialog({
            open: true,
            id,
        })
    }

    const confirmActivate = () => {
        if (activateDialog.id) {
            updateRoundRouteStatus({
                variables: {
                    priceId: activateDialog.id,
                    statusCode: 'ACT',
                },
            })
        }
    }

    const confirmDeactivate = () => {
        if (deactivateDialog.id) {
            updateRoundRouteStatus({
                variables: {
                    priceId: deactivateDialog.id,
                    statusCode: 'INA',
                },
            })
        }
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <ArrowsRightLeftIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Manage Round Trips Prices')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit round trips prices for your customers.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/price-config/round-trip-price/add')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Round Trip Price')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 mb-4 flex flex-col sm:flex-row flex-wrap gap-6">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Station From')}</label>
                            <Select value={stationFromFilter} onChange={e => setStationFromFilter(e.target.value)}>
                                <option value="">{t('Any Station')}</option>
                                {locations.map(v => (
                                    <option value={v.id}>{t(v.name.en ?? v.id)}</option>
                                ))}
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Station To')}</label>
                            <Select value={stationToFilter} onChange={e => setStationToFilter(e.target.value)}>
                                <option value="">{t('Any Station')}</option>
                                {locations.map(v => (
                                    <option value={v.id}>{t(v.name.en ?? v.id)}</option>
                                ))}
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Status')}</label>
                            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="">{t('All Statuses')}</option>
                                <option value="ACT">{t('Active')}</option>
                                <option value="INA">{t('Inactive')}</option>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Seat Layout')}</label>
                            <SeatLayoutDropdown
                                fields={{
                                    value: seatLayoutFilter,
                                    onChange: e => setSeatLayoutFilter(e.target.value),
                                }}
                                onChangeData={selected => setSeatLayoutFilter(selected.layout_id)}
                            />
                        </div>

                        <div className="flex-2 gap-2 justify-end">
                            <button
                                onClick={onClickClear}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mt-7"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />
            {loading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {error && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch round Tripss!')}</div>
                </div>
            )}

            {!loading && !error && filteredRoundTrips.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!loading && !error && filteredRoundTrips.length > 0 && (
                <>
                    <div className="table-custom rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)] w-full">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Start Station')}</TableHeader>
                                    <TableHeader>{t('End station')}</TableHeader>
                                    <TableHeader>{t('Seat Layout')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRoundTrips.map(roundRoute => (
                                    <TableRow
                                        key={roundRoute.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() =>
                                            navigate(`/price-config/round-trip-price/${roundRoute.price_id}`)
                                        }
                                        title={`roundRoute #${roundRoute.id}`}
                                    >
                                        <TableCell className="min-w-[200px] pr-12">
                                            <div className="flex items-center justify-between text-primary font-semibold">
                                                <div>
                                                    <span>{roundRoute.start_location?.country_details?.country}</span>
                                                    <div>{roundRoute.start_location.name?.en}</div>
                                                </div>

                                                <ArrowsRightLeftIcon className="h-4 w-4 text-gray-400 transition flex-shrink-0 mr-6" />
                                            </div>
                                        </TableCell>

                                        <TableCell className="min-w-[200px]">
                                            <div className="flex items-center text-primary font-semibold">
                                                <div>
                                                    <span>{roundRoute.end_location?.country_details?.country}</span>
                                                    <div>{roundRoute.end_location.name?.en}</div>
                                                </div>
                                            </div>
                                        </TableCell>

                                        <TableCell className="min-w-[200px]">
                                            <span className="font-semibold text-primary">
                                                <div className="mb-1">
                                                    <div> {roundRoute.seat_layout.layout_key} </div>
                                                    <div>
                                                        {' '}
                                                        ({roundRoute.seat_layout.num_rows} x{' '}
                                                        {roundRoute.seat_layout.num_columns})
                                                    </div>
                                                </div>
                                            </span>
                                        </TableCell>
                                        <TableCell>{getStatusBadge(roundRoute.status_code)}</TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`${roundRoute.price_id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`${roundRoute.price_id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    {roundRoute.status_code !== 'ACT' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleActivate(
                                                                        roundRoute.price_id,
                                                                        roundRoute.status_code
                                                                    )
                                                                }}
                                                            >
                                                                <PlusIcon />
                                                                <DropdownLabel>{t('Activate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    {roundRoute.status_code === 'ACT' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleDeactivate(
                                                                        roundRoute.price_id,
                                                                        roundRoute.status_code
                                                                    )
                                                                }}
                                                            >
                                                                <TrashIcon />
                                                                <DropdownLabel>{t('Deactivate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    {totalPages > 1 && (
                        <Pagination className="py-6">
                            <PaginationPrevious
                                onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                                isDisabled={page === 0}
                            />
                            <PaginationList>
                                {getPageRange(page, totalPages).map(index => (
                                    <PaginationPage
                                        key={index}
                                        current={index === page}
                                        onClick={() => handlePageChange(index)}
                                    >
                                        {index + 1}
                                    </PaginationPage>
                                ))}
                            </PaginationList>
                            <PaginationNext
                                onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                                isDisabled={page === totalPages - 1}
                            />
                        </Pagination>
                    )}
                    <Divider />
                </>
            )}
            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={successDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {activateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Activate Round Route Plan')}
                    message={`${t('Are you sure you want to activate')}?`}
                    onPositive={confirmActivate}
                    onNegative={() => setActivateDialog({ open: false, id: null, status: '' })}
                    positiveActionText={activateLoading ? t('Activating...') : t('Activate')}
                    negativeActionText={t('Cancel')}
                />
            )}
            {deactivateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Deactivate Round Route Plan')}
                    message={`${t('Are you sure you want to deactivate')}?`}
                    onPositive={confirmDeactivate}
                    onNegative={() => setDeactivateDialog({ open: false, id: null, status: '' })}
                    positiveActionText={activateLoading ? t('Deactivating...') : t('Deactivate')}
                    negativeActionText={t('Cancel')}
                />
            )}
        </>
    )
}
